<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import {
  RECONTACT_STATUSES,
  recontactStatusLabel,
} from '../helpers/whatsappRecontactTime';

const requireAssignee = defineModel('requireAssignee', {
  type: Boolean,
  default: true,
});
const requireClosedWindow = defineModel('requireClosedWindow', {
  type: Boolean,
  default: true,
});
const requireUnanswered = defineModel('requireUnanswered', {
  type: Boolean,
  default: true,
});
const eligibleStatuses = defineModel('eligibleStatuses', {
  type: Array,
  default: () => ['open', 'pending', 'snoozed'],
});

const { t } = useI18n();

const statusOptions = computed(() =>
  RECONTACT_STATUSES.map(status => ({
    value: status,
    label: recontactStatusLabel(status, t),
  }))
);

const setStatus = (status, checked) => {
  const selected = new Set(eligibleStatuses.value);
  if (checked) selected.add(status);
  else selected.delete(status);
  eligibleStatuses.value = RECONTACT_STATUSES.filter(item =>
    selected.has(item)
  );
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div>
      <p class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.RECONTACT.AUDIENCE.TITLE') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.RECONTACT.AUDIENCE.INTRO') }}
      </p>
    </div>
    <SettingsToggleSection
      v-model="requireAssignee"
      :header="t('INBOX_MGMT.RECONTACT.AUDIENCE.ASSIGNEE')"
      :description="t('INBOX_MGMT.RECONTACT.AUDIENCE.ASSIGNEE_DESC')"
    />
    <SettingsToggleSection
      v-model="requireClosedWindow"
      :header="t('INBOX_MGMT.RECONTACT.AUDIENCE.CLOSED_WINDOW')"
      :description="t('INBOX_MGMT.RECONTACT.AUDIENCE.CLOSED_WINDOW_DESC')"
    />
    <SettingsToggleSection
      v-model="requireUnanswered"
      :header="t('INBOX_MGMT.RECONTACT.AUDIENCE.UNANSWERED')"
      :description="t('INBOX_MGMT.RECONTACT.AUDIENCE.UNANSWERED_DESC')"
    />
    <div
      class="flex flex-col gap-3 rounded-xl px-4 py-3 outline outline-1 -outline-offset-1 outline-n-weak"
    >
      <div>
        <p class="mb-1 text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.RECONTACT.AUDIENCE.STATUSES') }}
        </p>
        <p class="mb-0 text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.RECONTACT.AUDIENCE.STATUSES_DESC') }}
        </p>
      </div>
      <div class="flex flex-col gap-2">
        <label
          v-for="status in statusOptions"
          :key="status.value"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <Checkbox
            :model-value="eligibleStatuses.includes(status.value)"
            @update:model-value="setStatus(status.value, $event)"
          />
          {{ status.label }}
        </label>
      </div>
    </div>
  </div>
</template>
